import clsx from 'clsx';
import { ReactNode } from 'react';
import { Cancel } from '@material-ui/icons';

interface Props extends BaseProps {
  classNames?: {
    icon?: string;
    label?: string;
    root?: string;
  };
  color?: ColorVariant;
  className?: string;
  clickable?: boolean;
  icon?: ReactNode;
  label: string;
  onClick?(): void;
  onDelete?(): void;
  size?: SizeVariant;
  square?: boolean;
  variant?: 'default' | 'outlined';
}

export const Chip = ({
  className,
  classNames,
  clickable = false,
  color = 'primary',
  icon = null,
  label,
  onClick,
  onDelete,
  size = 'medium',
  square = false,
  variant = 'default',
  ...rest
}: Props) => {
  const classes = useStyles(clickable);

  const renderDeleteIcon = () => {
    if (onDelete !== undefined)
      return (
        <Cancel
          className="!text-[1.15rem] opacity-70 hover:opacity-90 cursor-pointer"
          onClick={onDelete}
        />
      );
    else return null;
  };

  return (
    <div
      role={clickable && onDelete === undefined ? 'button' : undefined}
      className={clsx(
        className,
        classNames?.root,
        classes.root,
        classes.size[size],
        {
          ['rounded']: square,
          'rounded-full': !square,
          'cursor-default': onDelete !== undefined,
          [classes.variant.default[color]]: variant === 'default',
          [classes.variant.outlined]: variant === 'outlined',
        }
      )}
      onClick={onDelete === undefined ? onClick : undefined}
      {...rest}
    >
      {icon ? (
        <span className={clsx(classNames?.icon, classes.icon[size])}>
          {icon}
        </span>
      ) : null}
      <span
        className={clsx(
          classNames?.label,
          classes.label.root,
          classes.label.size[size]
        )}
      >
        {label}
      </span>
      {renderDeleteIcon()}
    </div>
  );
};

const useStyles = (clickable: boolean) => ({
  label: {
    root: 'px-1 select-none',
    size: {
      small: 'text-xs',
      medium: 'text-md',
      large: 'text-md',
    },
  },
  icon: {
    small: 'children:!text-[1.15rem]',
    medium: 'children:!text-[1.5rem]',
    large: 'children:!text-[1.6rem]',
  },
  root: 'flex items-center transition-all ease-in-out duration-200 px-1 border',
  variant: {
    default: {
      primary: clsx('bg-primary text-white border-transparent', {
        'hover:bg-primary/70': clickable,
      }),
      secondary: clsx('bg-secondary text-white border-transparent', {
        'hover:bg-secondary/70': clickable,
      }),
    },
    outlined: clsx('border border-border-light dark:border-border-dark', {
      'hover:bg-gray-500/10 dark:hover:bg-white/20': clickable,
    }),
  },
  size: {
    small: 'h-6',
    medium: 'h-8',
    large: 'h-9',
  },
});
